import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { praxisAnzeige } from '../lib/praxis.js';
import { useAuth } from '../auth/AuthContext.jsx';
import AktivitaetStatusBadge from '../components/AktivitaetStatusBadge.jsx';
import AnrufAmpel from '../components/AnrufAmpel.jsx';
import WiedervorlageMarkierung from '../components/WiedervorlageMarkierung.jsx';
import {
  useAktivitaetChangeListener,
  useAktivitaetModal,
} from '../components/AktivitaetModalProvider.jsx';
import {
  TYP_ICON,
  TYP_OPTIONS,
  formatDatum,
  istUeberfaellig,
  vergleicheAktivitaetenNachFaelligkeit,
} from '../lib/aktivitaet.js';

const FILTER_OPTIONS = [
  { value: 'offen', label: 'Offen' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'wiedervorlagen', label: 'Wiedervorlagen' },
  { value: 'erledigt', label: 'Erledigt' },
  { value: 'alle', label: 'Alle' },
];

function tageszeitGruss() {
  const h = new Date().getHours();
  if (h < 12) return 'Guten Morgen';
  if (h < 14) return 'Guten Mittag';
  if (h < 18) return 'Guten Nachmittag';
  return 'Guten Abend';
}

function datumLang() {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function heuteIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function vornameVon(user) {
  if (!user) return '';
  const v = user.vorname?.trim();
  if (v) return v;
  const n = user.name?.trim();
  if (n) return n.split(/\s+/)[0];
  if (user.email) return user.email.split('@')[0];
  return '';
}

// Karte für eine einzelne Aufgabe — links Praxisname mit Ampel-Punkt und
// Aufgaben-Titel, rechts Datum/Uhrzeit und Status-Pille. Klick auf die
// Karte öffnet das globale Bearbeiten-Modal; der Praxis-Link und die
// Status-Pille stoppen die Klick-Propagation selbst.
function AufgabenKarte({ aktivitaet, ueberfaellig, onClick, onStatusChange }) {
  const praxis = aktivitaet.expand?.praxis;
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg bg-white p-4 ring-1 ring-slate-200 transition hover:ring-slate-300 ${
        ueberfaellig ? 'border-l-4 border-red-400' : ''
      }`}
      title="Klicken zum Bearbeiten"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {praxis && <AnrufAmpel praxis={praxis} size="sm" />}
            {praxis ? (
              <Link
                to={`/praxen/${praxis.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate text-sm font-semibold text-slate-900 hover:text-primary-700 hover:underline"
              >
                {praxisAnzeige(praxis)}
              </Link>
            ) : (
              <span className="truncate text-sm font-semibold text-slate-900">—</span>
            )}
          </div>
          <p className="mt-1.5 flex items-start gap-1.5 text-sm text-slate-700">
            <span className="leading-none" aria-hidden="true">
              {TYP_ICON[aktivitaet.typ] ?? '•'}
            </span>
            <span className="flex-1">{aktivitaet.titel}</span>
          </p>
          {(aktivitaet.ist_wiedervorlage || aktivitaet.wiedervorlage_am) && (
            <div className="mt-1.5">
              <WiedervorlageMarkierung
                ist_wiedervorlage={aktivitaet.ist_wiedervorlage}
                wiedervorlage_am={aktivitaet.wiedervorlage_am}
                variant="both"
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {aktivitaet.faellig_am && (
            <div className="text-right text-xs text-slate-500">
              <div>{formatDatum(aktivitaet.faellig_am)}</div>
              {aktivitaet.uhrzeit && (
                <div className="font-medium text-slate-700">{aktivitaet.uhrzeit}</div>
              )}
            </div>
          )}
          <AktivitaetStatusBadge
            status={aktivitaet.status}
            onChange={(next) => onStatusChange(aktivitaet, next)}
          />
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ titel, count, tone = 'slate' }) {
  const titelClass = tone === 'red' ? 'text-red-600' : 'text-slate-700';
  const badgeClass =
    tone === 'red'
      ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
      : 'bg-slate-100 text-slate-700';
  return (
    <header className="mb-3 flex items-center gap-2">
      <h2 className={`text-sm font-semibold uppercase tracking-wider ${titelClass}`}>
        {titel}
      </h2>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
        {count}
      </span>
    </header>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const aktivitaetModal = useAktivitaetModal();
  const [filter, setFilter] = useState('offen');
  const [search, setSearch] = useState('');
  const [typFilter, setTypFilter] = useState('alle');
  const [praxisFilter, setPraxisFilter] = useState('alle');
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [counts, setCounts] = useState({
    offen: 0,
    in_bearbeitung: 0,
    erledigt: 0,
    wiedervorlagen: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const list = await pb.collection('aktivitaeten').getFullList({
        filter: `zustaendig = "${user.id}"`,
        expand: 'praxis',
      });
      // Client-seitig sortieren: Aufgaben mit Uhrzeit kommen innerhalb eines
      // Tages chronologisch vor den unzeitigen, Aufgaben ohne Datum landen
      // ganz hinten — beides würde die String-Sort von PB nicht hinkriegen.
      list.sort(vergleicheAktivitaetenNachFaelligkeit);
      setAktivitaeten(list);

      const next = { offen: 0, in_bearbeitung: 0, erledigt: 0, wiedervorlagen: 0 };
      for (const a of list) {
        if (a.status in next) next[a.status] += 1;
        // Wiedervorlagen-Zähler im Dashboard zählt offene Wiedervorlagen —
        // sowohl im neuen Modell (ist_wiedervorlage) als auch Altbestand
        // (wiedervorlage_am), damit beide Welten sichtbar bleiben.
        if (
          (a.ist_wiedervorlage || a.wiedervorlage_am) &&
          a.status !== 'erledigt'
        ) {
          next.wiedervorlagen += 1;
        }
      }
      setCounts(next);
    } catch (err) {
      setError(err?.message || 'Aktivitäten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Wenn das globale Aktivitäts-Modal etwas speichert, neu laden.
  useAktivitaetChangeListener(load);

  // Eindeutige Praxen aus den Aufgaben des Users — alphabetisch.
  const praxenOptionen = useMemo(() => {
    const map = new Map();
    for (const a of aktivitaeten) {
      const praxis = a.expand?.praxis;
      if (praxis && !map.has(praxis.id)) {
        map.set(praxis.id, praxis.name);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'de')
    );
  }, [aktivitaeten]);

  // Falls die ausgewählte Praxis nach einem Reload nicht mehr in den
  // Aufgaben vorkommt, Filter zurücksetzen — sonst wäre die Liste leer
  // ohne erkennbaren Grund.
  useEffect(() => {
    if (praxisFilter !== 'alle' && !praxenOptionen.some((p) => p.id === praxisFilter)) {
      setPraxisFilter('alle');
    }
  }, [praxenOptionen, praxisFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return aktivitaeten.filter((a) => {
      // Filter "Wiedervorlagen" zeigt offene Wiedervorlagen — neues Modell
      // (ist_wiedervorlage) plus Altbestand (wiedervorlage_am), beide nur
      // wenn die Aktivität nicht erledigt ist.
      if (filter === 'wiedervorlagen') {
        if (a.status === 'erledigt') return false;
        if (!a.ist_wiedervorlage && !a.wiedervorlage_am) return false;
      } else if (filter !== 'alle' && a.status !== filter) {
        return false;
      }
      if (typFilter !== 'alle' && a.typ !== typFilter) return false;
      if (praxisFilter !== 'alle' && a.praxis !== praxisFilter) return false;
      if (q && !(a.titel || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [aktivitaeten, filter, typFilter, praxisFilter, search]);

  // Splitten in drei Buckets nach Fälligkeitsdatum:
  //   - Überfällig: vor heute UND nicht erledigt
  //   - Heute:      heute fällig
  //   - Später:     in der Zukunft oder ohne Datum
  const { ueberfaellige, heute, spaeter } = useMemo(() => {
    const heuteStr = heuteIso();
    const ueb = [];
    const heu = [];
    const sp = [];
    for (const a of filtered) {
      if (istUeberfaellig(a.faellig_am, a.status)) {
        ueb.push(a);
      } else if (a.faellig_am === heuteStr) {
        heu.push(a);
      } else {
        sp.push(a);
      }
    }
    return { ueberfaellige: ueb, heute: heu, spaeter: sp };
  }, [filtered]);

  async function handleStatusChange(aktivitaet, nextStatus) {
    try {
      const updated = await pb
        .collection('aktivitaeten')
        .update(aktivitaet.id, { status: nextStatus }, { expand: 'praxis' });
      setAktivitaeten((prev) => prev.map((a) => (a.id === aktivitaet.id ? updated : a)));
      setCounts((prev) => ({
        ...prev,
        [aktivitaet.status]: Math.max(0, (prev[aktivitaet.status] ?? 0) - 1),
        [nextStatus]: (prev[nextStatus] ?? 0) + 1,
      }));
    } catch (err) {
      alert(err?.message || 'Status konnte nicht aktualisiert werden.');
    }
  }

  const vorname = vornameVon(user);
  const grussText = vorname ? `${tageszeitGruss()}, ${vorname}!` : `${tageszeitGruss()}!`;

  function renderKarte(a) {
    return (
      <AufgabenKarte
        key={a.id}
        aktivitaet={a}
        ueberfaellig={istUeberfaellig(a.faellig_am, a.status)}
        onClick={() => aktivitaetModal.openEdit({ aktivitaet: a })}
        onStatusChange={handleStatusChange}
      />
    );
  }

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{grussText} 👋</h1>
        <p className="mt-1 text-sm text-slate-500">{datumLang()}</p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <input
          type="search"
          placeholder="Suche nach Titel …"
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <label
              htmlFor="typ-filter"
              className="text-xs font-semibold uppercase tracking-wider text-slate-500"
            >
              Typ:
            </label>
            <select
              id="typ-filter"
              className="input py-1.5"
              value={typFilter}
              onChange={(e) => setTypFilter(e.target.value)}
            >
              <option value="alle">Alle</option>
              {TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="praxis-filter"
              className="text-xs font-semibold uppercase tracking-wider text-slate-500"
            >
              Praxis:
            </label>
            <select
              id="praxis-filter"
              className="input py-1.5"
              value={praxisFilter}
              onChange={(e) => setPraxisFilter(e.target.value)}
            >
              <option value="alle">Alle</option>
              {praxenOptionen.map((p) => (
                <option key={p.id} value={p.id}>
                  {praxisAnzeige(p)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {FILTER_OPTIONS.map((opt) => {
            const isActive = filter === opt.value;
            const count =
              opt.value === 'alle'
                ? aktivitaeten.length
                : counts[opt.value] ?? 0;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50'
                }`}
              >
                {opt.label}
                <span
                  className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs ${
                    isActive ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Lade …</p>}

      {!loading && (
        <div className="space-y-10">
          {ueberfaellige.length > 0 && (
            <section>
              <SectionHeader
                titel="Überfällig"
                count={ueberfaellige.length}
                tone="red"
              />
              <div className="space-y-3">{ueberfaellige.map(renderKarte)}</div>
            </section>
          )}

          <section>
            <SectionHeader titel="Heute" count={heute.length} />
            {heute.length === 0 ? (
              <p className="text-sm text-slate-400">Heute steht nichts an.</p>
            ) : (
              <div className="space-y-3">{heute.map(renderKarte)}</div>
            )}
          </section>

          <section>
            <SectionHeader titel="Diese Woche / Später" count={spaeter.length} />
            {spaeter.length === 0 ? (
              <p className="text-sm text-slate-400">Nichts in den nächsten Tagen.</p>
            ) : (
              <div className="space-y-3">{spaeter.map(renderKarte)}</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
